import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiHome, FiBook, FiHelpCircle, FiPlay, FiBarChart2, FiUsers, FiLogOut, FiMenu, FiX, FiFolder, FiShield } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND, Initials } from './ui';

const studentLinks = [
  { to: '/dashboard', label: 'Dashboard', icon: FiHome },
  { to: '/lezioni', label: 'Lessons', icon: FiBook },
  { to: '/quiz', label: 'Quiz', icon: FiHelpCircle },
  { to: '/giochi', label: 'Games', icon: FiPlay },
  { to: '/statistiche', label: 'Stats', icon: FiBarChart2 },
];

const adminLinks = [
  { to: '/admin', label: 'Dashboard', icon: FiHome, exact: true },
  { to: '/admin/studenti', label: 'Students', icon: FiUsers },
  { to: '/admin/corsi', label: 'Courses', icon: FiBook },
  { to: '/admin/quiz', label: 'Quiz', icon: FiHelpCircle },
  { to: '/admin/risorse', label: 'Resources', icon: FiFolder },
];

export function BrandMark({ size = 38, showSub = true, to }) {
  const inner = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 11, flexShrink: 0 }} aria-label="C.O.R.E. for Schools">
      <img src={BRAND.owl} alt="" aria-hidden style={{ width: size, height: size, filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.45))' }} />
      <span style={{ fontWeight: 800, letterSpacing: '0.28em', color: BRAND.goldLight, fontSize: size * 0.4, lineHeight: 1 }}>C.O.R.E.</span>
      {showSub && <span className="core-nav-sub" style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: size * 0.5, color: BRAND.muted, marginLeft: -4, lineHeight: 1, whiteSpace: 'nowrap' }}>for Schools</span>}
    </span>
  );
  return to ? <Link to={to} style={{ display: 'inline-flex' }}>{inner}</Link> : inner;
}

function NavItem({ to, label, icon: Icon, active, onClick, mobile = false }) {
  return (
    <li>
      <Link
        to={to}
        onClick={onClick}
        style={{
          position: 'relative', display: 'flex', alignItems: 'center', gap: 9,
          padding: mobile ? '13px 16px' : '8px 15px', borderRadius: mobile ? 14 : 999,
          fontWeight: 700, fontSize: mobile ? '1rem' : '0.92rem',
          color: active ? BRAND.ivory : BRAND.muted,
          background: active ? 'rgba(205,155,80,0.12)' : 'transparent',
          transition: 'background .2s, color .2s',
        }}
        onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(249,240,231,0.06)'; e.currentTarget.style.color = BRAND.ivory; } }}
        onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = BRAND.muted; } }}
      >
        <Icon size={17} style={{ color: active ? BRAND.goldLight : 'currentColor' }} aria-hidden />
        {label}
        {active && !mobile && <span aria-hidden style={{ position: 'absolute', left: 15, right: 15, bottom: 3, height: 2, borderRadius: 2, background: BRAND.gold }} />}
      </Link>
    </li>
  );
}

export default function Navbar() {
  const { user, logout, isAdmin, isSuperadmin } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const links = isSuperadmin
    ? [{ to: '/platform', label: 'Platform', icon: FiShield }, ...adminLinks]
    : isAdmin ? adminLinks : studentLinks;
  const name = user?.displayName || user?.name || (isAdmin ? 'Admin' : 'Student');

  const isActive = (item) => item.exact ? pathname === item.to : (pathname === item.to || pathname.startsWith(item.to + '/'));

  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 900) setMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const lock = menuOpen && window.matchMedia('(max-width: 900px)').matches;
    document.body.style.overflow = lock ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const handleLogout = () => { logout(); setMenuOpen(false); navigate('/login'); };

  const logoutBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 999,
    border: `1px solid ${BRAND.gold}`, color: BRAND.goldLight, background: 'transparent',
    fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer', fontFamily: BRAND.font, transition: 'background .2s',
  };

  return (
    <>
      <style>{`
        .core-nav-desktop { display: flex; }
        .core-nav-burger { display: none; }
        .core-nav-drawer { display: none; }
        @media (max-width: 1180px) { .core-nav-sub { display: none !important; } .core-nav-desktop ul a { padding: 8px 11px !important; } .core-nav-role { display: none !important; } }
        @media (max-width: 1080px) { .core-nav-name { display: none !important; } .core-nav-user { padding: 3px !important; } .core-nav-logout-label { display: none !important; } .core-nav-logout { padding: 8px 11px !important; } }
        @media (max-width: 900px) {
          .core-nav-desktop { display: none !important; }
          .core-nav-burger { display: inline-flex !important; }
          .core-nav-drawer { display: flex !important; }
          .core-nav-sub { display: none !important; }
        }
      `}</style>
      <header role="banner" style={{ position: 'sticky', top: 0, zIndex: 1000, height: 64, background: 'rgba(7,20,45,0.92)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderBottom: `1px solid ${BRAND.line}`, fontFamily: BRAND.font }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', height: '100%', padding: '0 clamp(16px,2.6vw,32px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <BrandMark to={isAdmin ? '/admin' : '/dashboard'} />

          <nav className="core-nav-desktop" aria-label="Main" style={{ flex: '0 1 auto', justifyContent: 'center' }}>
            <ul style={{ display: 'flex', alignItems: 'center', gap: 4, listStyle: 'none', margin: 0, padding: 0 }}>
              {links.map(item => <NavItem key={item.to} {...item} active={isActive(item)} />)}
            </ul>
          </nav>

          <div className="core-nav-desktop" style={{ alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span className="core-nav-user" title={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '4px 12px 4px 4px', border: `1px solid ${BRAND.line}`, borderRadius: 999, fontSize: '0.86rem', fontWeight: 700, color: BRAND.ivory, maxWidth: 220 }}>
              <Initials name={name} size={28} />
              <span className="core-nav-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              {isAdmin && <span className="core-nav-role" style={{ fontSize: '0.62rem', letterSpacing: '0.12em', color: BRAND.goldLight, fontWeight: 800 }}>{isSuperadmin ? 'OWNER' : 'ADMIN'}</span>}
            </span>
            <button type="button" className="core-nav-logout" style={logoutBtn} onClick={handleLogout} aria-label="Log out"
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(205,155,80,0.14)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <FiLogOut size={15} aria-hidden /> <span className="core-nav-logout-label">Log out</span>
            </button>
          </div>

          <button type="button" className="core-nav-burger" aria-expanded={menuOpen} aria-controls="core-nav-drawer" aria-label={menuOpen ? 'Close menu' : 'Open menu'} onClick={() => setMenuOpen(o => !o)}
            style={{ alignItems: 'center', justifyContent: 'center', width: 42, height: 42, borderRadius: 12, background: 'rgba(249,240,231,0.08)', border: `1px solid ${BRAND.line}`, color: BRAND.ivory, cursor: 'pointer' }}>
            {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
        </div>
      </header>

      {menuOpen && <div role="presentation" onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(7,20,45,0.6)', zIndex: 998, backdropFilter: 'blur(4px)' }} />}

      <nav id="core-nav-drawer" aria-label="Mobile menu" className="core-nav-drawer"
        style={{ position: 'fixed', top: 0, right: 0, width: 'min(320px, 88vw)', height: '100vh', background: BRAND.navy800, borderLeft: `1px solid ${BRAND.line}`, zIndex: 999, padding: '20px 18px', boxShadow: '0 24px 70px rgba(0,0,0,0.5)', transition: 'transform .28s ease', transform: menuOpen ? 'translateX(0)' : 'translateX(100%)', flexDirection: 'column', overflowY: 'auto', fontFamily: BRAND.font }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <BrandMark size={32} showSub={false} />
          <button type="button" aria-label="Close" onClick={() => setMenuOpen(false)} style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(249,240,231,0.08)', border: `1px solid ${BRAND.line}`, color: BRAND.ivory, display: 'grid', placeItems: 'center', cursor: 'pointer' }}><FiX size={20} /></button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22, padding: '10px 12px', border: `1px solid ${BRAND.line}`, borderRadius: 14 }}>
          <Initials name={name} size={34} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
            <div style={{ fontSize: '0.72rem', color: BRAND.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{isSuperadmin ? 'Platform owner' : isAdmin ? 'Administrator' : 'Student'}</div>
          </div>
        </div>
        <ul style={{ listStyle: 'none', margin: '18px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {links.map(item => <NavItem key={item.to} {...item} mobile active={isActive(item)} onClick={() => setMenuOpen(false)} />)}
        </ul>
        <div style={{ marginTop: 'auto', paddingTop: 20, borderTop: `1px solid ${BRAND.line}` }}>
          <button type="button" style={{ ...logoutBtn, width: '100%', justifyContent: 'center', padding: '12px' }} onClick={handleLogout}><FiLogOut size={16} /> Log out</button>
        </div>
      </nav>
    </>
  );
}
